import type { Presence, SelectablePresence } from '@batiflow/ui';
import { useCallback } from 'react';
import { useStore } from 'zustand';
import { useRuntime } from '@/app/providers/runtime-provider';
import { selectMessageOf, selectOwnMessage, selectOwnPresence, selectPresenceOf } from './store';

/**
 * The presence to show on the avatar of `userId`; undefined (no dot) when it is not known: the
 * person is not a contact the server reports, or the realtime connection is not open.
 */
export function usePresenceOf(userId: string | null | undefined): Presence | undefined {
  const { realtimeStore } = useRuntime();
  return useStore(realtimeStore, (state) => (userId ? selectPresenceOf(state, userId) : undefined));
}

/** The person's own presence (shown on the account avatar) and the way to choose a status. */
export function useOwnPresence(): {
  presence: Presence | undefined;
  choose: (status: SelectablePresence) => void;
} {
  const { realtimeStore } = useRuntime();
  const presence = useStore(realtimeStore, selectOwnPresence);
  const choose = useCallback(
    (status: SelectablePresence) => {
      realtimeStore.getState().choose(status);
    },
    [realtimeStore],
  );
  return { presence, choose };
}

/** The status message of `userId` (plain text), when they are online and set one. */
export function usePresenceMessageOf(userId: string | null | undefined): string | undefined {
  const { realtimeStore } = useRuntime();
  return useStore(realtimeStore, (state) => (userId ? selectMessageOf(state, userId) : undefined));
}

/** The person's own status message and the way to set it (null or empty clears it). */
export function useOwnStatusMessage(): {
  message: string | undefined;
  setMessage: (message: string | null) => void;
} {
  const { realtimeStore } = useRuntime();
  const message = useStore(realtimeStore, selectOwnMessage);
  const setMessage = useCallback(
    (next: string | null) => {
      realtimeStore.getState().setOwnMessage(next);
    },
    [realtimeStore],
  );
  return { message, setMessage };
}
